/**
 * VAT (ALV) utilities for Verstaani.
 *
 * All prices in the system are stored VAT-exclusive (net).
 * These utilities help with display and calculations.
 */

// Finnish VAT rates (as of 2025)
export const VAT_RATES = [
  { value: 25.5, label: '25,5 %', labelEn: '25.5%', description: 'Yleinen', descriptionEn: 'Standard' },
  { value: 13.5, label: '13,5 %', labelEn: '13.5%', description: 'Elintarvikkeet', descriptionEn: 'Food' },
  { value: 10, label: '10 %', labelEn: '10%', description: 'Kirjat, lääkkeet', descriptionEn: 'Books, medicine' },
  { value: 0, label: '0 %', labelEn: '0%', description: 'ALV 0', descriptionEn: 'VAT exempt' },
];

export const DEFAULT_VAT_RATE = 25.5;

// Round half away from zero, matching backend Python ROUND_HALF_UP.
// Math.round alone rounds halves toward +Infinity (-100.5 -> -100), which would
// make a credit line and its positive twin differ by a cent at exact halves.
// Identical to Math.round for non-negative inputs.
const roundHalfAwayFromZero = (x) => Math.sign(x) * Math.round(Math.abs(x));

/**
 * Round a monetary value to 2 decimal places.
 *
 * Uses arithmetic that avoids IEEE 754 floating-point edge cases
 * (e.g. 75 * 1.255 = 94.12499... instead of 94.125 due to 0.255
 * not being exactly representable in binary).
 *
 * @param {number|string} value - Value to round
 * @returns {number} Rounded to 2 decimal places
 */
export const roundMoney = (value) => roundHalfAwayFromZero((parseFloat(value) || 0) * 100) / 100;

/**
 * Calculate VAT-inclusive price from VAT-exclusive price.
 *
 * Uses price * (100 + vatRate) / 100 instead of price * (1 + vatRate/100)
 * to avoid floating-point imprecision (vatRate/100 can't be exactly
 * represented for rates like 25.5%).
 *
 * @param {number|string} priceExclVat - Price excluding VAT
 * @param {number} vatRate - VAT rate percentage (default 25.5)
 * @returns {number} Price including VAT
 */
export const calcPriceInclVat = (priceExclVat, vatRate = DEFAULT_VAT_RATE) => {
  const price = parseFloat(priceExclVat) || 0;
  const rate = parseFloat(vatRate) || 0;
  return roundHalfAwayFromZero(price * (100 + rate)) / 100;
};

/**
 * Resolve an ex-VAT price for display under the organization's price display
 * mode (`default_price_includes_vat`). Catalogue prices are always stored ex-VAT;
 * screens that list them must show them the same way the parts registry does.
 * @param {number|string|null} priceExclVat - Stored ex-VAT price
 * @param {boolean} showInclVat - Organization displays VAT-inclusive prices
 * @param {number} vatRate - VAT rate percentage (default 25.5)
 * @returns {number|null} Price in the display basis, or null when there is none
 */
export const priceForDisplay = (priceExclVat, showInclVat, vatRate = DEFAULT_VAT_RATE) => {
  if (priceExclVat == null || priceExclVat === '') return null;
  return showInclVat ? calcPriceInclVat(priceExclVat, vatRate) : parseFloat(priceExclVat);
};

/**
 * Calculate VAT-exclusive price from VAT-inclusive price.
 * @param {number|string} priceInclVat - Price including VAT
 * @param {number} vatRate - VAT rate percentage (default 25.5)
 * @returns {number} Price excluding VAT
 */
export const calcPriceExclVat = (priceInclVat, vatRate = DEFAULT_VAT_RATE) => {
  const price = parseFloat(priceInclVat) || 0;
  const rate = parseFloat(vatRate) || 0;
  return roundHalfAwayFromZero(price / (100 + rate) * 10000) / 100;
};

/**
 * Calculate VAT amount from VAT-exclusive price.
 * @param {number|string} priceExclVat - Price excluding VAT
 * @param {number} vatRate - VAT rate percentage (default 25.5)
 * @returns {number} VAT amount (rounded to 2 decimals)
 */
export const calcVatAmount = (priceExclVat, vatRate = DEFAULT_VAT_RATE) => {
  const price = parseFloat(priceExclVat) || 0;
  const rate = parseFloat(vatRate) || 0;
  return roundHalfAwayFromZero(price * rate) / 100;
};

/**
 * Format price for display (Finnish style: comma as decimal separator).
 *
 * Note: renders an ASCII hyphen for negatives, while Intl-based formatters
 * (e.g. formatCurrency in InvoiceDetailView) render U+2212. Known
 * inconsistency; E2E assertions must match the formatter in use.
 *
 * @param {number|string} price - Price to format
 * @param {number} decimals - Number of decimal places (default 2)
 * @returns {string} Formatted price string
 */
export const formatPrice = (price, decimals = 2) => {
  const num = parseFloat(price) || 0;
  let fixed = num.toFixed(decimals);
  // Avoid "-0,00" for tiny negative values
  if (parseFloat(fixed) === 0) {
    fixed = (0).toFixed(decimals);
  }
  return fixed.replace('.', ',');
};

/**
 * Format price with currency symbol.
 * @param {number|string} price - Price to format
 * @returns {string} Formatted price with € symbol
 */
export const formatPriceWithCurrency = (price) => {
  return `${formatPrice(price)} €`;
};

/**
 * Format a euro amount for report screens: Finnish locale, thousands grouped, € suffix
 * (1 102,16 €). Returns '-' for a missing value. The report tabs each carried a copy of
 * this Intl formatter; new report code uses this one.
 * @param {number|string|null|undefined} value - Amount in euros
 * @returns {string} Formatted amount
 */
export const formatEuro = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

/**
 * Get VAT rate option by value.
 * @param {number} value - VAT rate value
 * @returns {object|undefined} VAT rate option object
 */
export const getVatRateOption = (value) => {
  return VAT_RATES.find(rate => rate.value === parseFloat(value));
};

/**
 * Get VAT rate label for display.
 * @param {number} value - VAT rate value
 * @param {string} lang - Language code ('fi' or 'en')
 * @returns {string} VAT rate label
 */
export const getVatRateLabel = (value, lang = 'fi') => {
  const option = getVatRateOption(value);
  if (!option) return `${value} %`;
  return lang === 'fi' ? option.label : option.labelEn;
};
